import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { 
  ArrowLeft,
  Shield, 
  Check, 
  X, 
  Users, 
  Info,
  ChevronRight,
  ChevronDown,
  User,
  UserCheck,
  Mail,
  Phone,
  CheckCircle,
  XCircle,
  Save,
  RotateCcw
} from 'lucide-react';
import { employeesAPI, permissionsAPI } from '../services/api';
import { permissionConfig } from '../config/permissions';

const EmployeePermissions = () => {
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [expandedModules, setExpandedModules] = useState({});
  const [employeePermissions, setEmployeePermissions] = useState([]);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const queryClient = useQueryClient();

  // Check user permissions for assignment actions
  const canViewAssignments = hasPermission('employees_assign_view');
  const canAddAssignments = hasPermission('employees_assign_add');
  const canDeleteAssignments = hasPermission('employees_assign_delete');

  // Check if user is super admin (developer)
  const { employee: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'developer';

  // Check if user has any assignment permission
  const hasAnyAssignmentPermission = isSuperAdmin || canViewAssignments || canAddAssignments || canDeleteAssignments;

  // Redirect if user doesn't have permission to view assignments
  useEffect(() => {
    if (!hasAnyAssignmentPermission) {
      toast.error('You do not have permission to manage employee permissions');
      navigate('/employees');
    }
  }, [hasAnyAssignmentPermission, navigate]);

  // Fetch employee details
  const { data: employeeData, isLoading: employeeLoading } = useQuery(
    ['employee', employeeId],
    () => employeesAPI.getById(employeeId),
    {
      onSuccess: (data) => {
        const employee = data.data.data;
        const currentPermissions = employee.permissions?.map(p => p.name) || [];
        setEmployeePermissions(currentPermissions);
      },
      onError: (error) => {
        toast.error('Failed to load employee details');
        navigate('/employees');
      }
    }
  );

  // Fetch assignable permissions (filtered by current user's capabilities)
  const { data: permissionsData, isLoading: permissionsLoading } = useQuery(
    ['assignable-permissions'],
    () => permissionsAPI.getAssignable()
  );

  // Assign permission mutation
  const assignPermissionMutation = useMutation(
    ({ employeeUuid, permissionUuid }) => permissionsAPI.assignToEmployee(employeeUuid, permissionUuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['employee', employeeId]);
        queryClient.invalidateQueries(['employees']);
        queryClient.invalidateQueries(['assignable-permissions']);
        toast.success('Permission assigned successfully!');
        setUnsavedChanges(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to assign permission');
      }
    }
  );

  // Remove permission mutation
  const removePermissionMutation = useMutation(
    ({ employeeUuid, permissionUuid }) => permissionsAPI.removeFromEmployee(employeeUuid, permissionUuid),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['employee', employeeId]);
        queryClient.invalidateQueries(['employees']);
        queryClient.invalidateQueries(['assignable-permissions']);
        toast.success('Permission removed successfully!');
        setUnsavedChanges(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to remove permission');
      }
    }
  );



  const employee = employeeData?.data?.data;
  const availablePermissions = Array.isArray(permissionsData?.data?.data) ? permissionsData.data.data : [];
  
  // Debug logging
  console.log('=== EMPLOYEE PERMISSIONS DEBUG ===');
  console.log('permissionsData:', permissionsData);
  console.log('availablePermissions:', availablePermissions);
  console.log('availablePermissions.length:', availablePermissions.length);
  console.log('isSuperAdmin:', isSuperAdmin);
  console.log('currentUser:', currentUser);
  console.log('currentUser.permissions:', currentUser?.permissions);
  console.log('currentUser.permissions length:', currentUser?.permissions?.length);
  console.log('currentUser.permissions names:', currentUser?.permissions?.map(p => p.name));
  console.log('canViewAssignments:', canViewAssignments);
  console.log('canAddAssignments:', canAddAssignments);
  console.log('canDeleteAssignments:', canDeleteAssignments);
  
  // Filter permission config to only show assignable permissions
  const getFilteredPermissionConfig = () => {
    console.log('=== getFilteredPermissionConfig() called ===');
    console.log('Original permissionConfig:', permissionConfig);
    console.log('Original permissionConfig.modules:', permissionConfig.modules);
    console.log('Original module keys:', Object.keys(permissionConfig.modules));
    
    const filteredConfig = { ...permissionConfig };
    
    // If super admin, show all permissions
    if (isSuperAdmin) {
      console.log('Super admin detected, showing all permissions');
      return filteredConfig;
    }
    
    // For regular users, filter based on assignable permissions
    const assignablePermissionNames = availablePermissions.map(p => p.name);
    console.log('assignablePermissionNames:', assignablePermissionNames);
    console.log('availablePermissions.length:', availablePermissions.length);
    
    // If no assignable permissions are loaded yet, show all permissions for users with assignment rights
    // This prevents the UI from showing empty modules while permissions are loading
    if (assignablePermissionNames.length === 0 && hasAnyAssignmentPermission && !permissionsLoading) {
      console.log('No assignable permissions found but user has assignment rights, showing all permissions');
      return filteredConfig;
    }
    
    // If still loading, return empty modules
    if (permissionsLoading) {
      console.log('Still loading permissions');
      return { ...filteredConfig, modules: {} };
    }
    
    Object.keys(filteredConfig.modules).forEach(moduleKey => {
      const module = filteredConfig.modules[moduleKey];
      console.log(`Processing module: ${moduleKey}, permissions:`, module.permissions.map(p => p.key));
      console.log(`assignablePermissionNames:`, assignablePermissionNames);
      
      const filteredPermissions = module.permissions.filter(p => {
        const isAssignable = assignablePermissionNames.includes(p.key);
        console.log(`  Permission ${p.key} is assignable: ${isAssignable}`);
        return isAssignable;
      });
      
      console.log(`${moduleKey} filteredPermissions:`, filteredPermissions.map(p => p.key));
      
      // If no permissions in this module are assignable, remove the module
      if (filteredPermissions.length === 0) {
        console.log(`Removing module ${moduleKey} - no assignable permissions`);
        delete filteredConfig.modules[moduleKey];
      } else {
        console.log(`Keeping module ${moduleKey} with ${filteredPermissions.length} permissions`);
        filteredConfig.modules[moduleKey] = {
          ...module,
          permissions: filteredPermissions
        };
      }
    });
    
    return filteredConfig;
  };
  
  const filteredPermissionConfig = getFilteredPermissionConfig();
  console.log('=== FILTERED CONFIG ===');
  console.log('filteredPermissionConfig:', filteredPermissionConfig);
  console.log('filteredPermissionConfig.modules:', filteredPermissionConfig.modules);
  console.log('Number of modules:', Object.keys(filteredPermissionConfig.modules).length);
  console.log('hasAnyAssignmentPermission:', hasAnyAssignmentPermission);
  console.log('isSuperAdmin:', isSuperAdmin);
  console.log('availablePermissions:', availablePermissions);
  console.log('permissionsLoading:', permissionsLoading);
  
  // Initialize expanded modules when filtered config is ready
  useEffect(() => {
    if (Object.keys(filteredPermissionConfig.modules).length > 0) {
      const initialExpanded = {};
      Object.keys(filteredPermissionConfig.modules).forEach(moduleKey => {
        initialExpanded[moduleKey] = true;
      });
      setExpandedModules(initialExpanded);
    }
  }, [permissionsData]);

  // Check if employee has specific permission
  const employeeHasPermission = (permissionKey) => {
    return employeePermissions.includes(permissionKey);
  };

  // Get permission UUID by key
  const getPermissionUuid = (permissionKey) => {
    const permission = allPermissions.find(p => p.name === permissionKey);
    return permission?.uuid;
  };

  // For super admin or users with assignment permissions, fetch all permissions as fallback
  const { data: allPermissionsData } = useQuery(
    ['all-permissions'],
    () => permissionsAPI.getAll(),
    {
      enabled: isSuperAdmin || hasAnyAssignmentPermission,
      onSuccess: (data) => {
        console.log('All permissions loaded:', data);
      }
    }
  );

  // Get all permissions for super admin, or available permissions for regular users
  const getAllPermissions = () => {
    if (isSuperAdmin && allPermissionsData?.data?.data) {
      return Array.isArray(allPermissionsData.data.data) ? allPermissionsData.data.data : [];
    }
    
    // For regular users, use availablePermissions if they exist
    if (availablePermissions.length > 0) {
      return availablePermissions;
    }
    
    // If no assignable permissions but user has assignment rights, 
    // fall back to all permissions from the backend
    if (hasAnyAssignmentPermission && allPermissionsData?.data?.data) {
      return Array.isArray(allPermissionsData.data.data) ? allPermissionsData.data.data : [];
    }
    
    return availablePermissions;
  };

  const allPermissions = getAllPermissions();

  // Toggle permission
  const togglePermission = async (permissionKey) => {
    const permissionUuid = getPermissionUuid(permissionKey);
    if (!permissionUuid) return;

    const currentlyHasPermission = employeePermissions.includes(permissionKey);

    // Check if user has permission to perform this action (super admin can do everything)
    if (!isSuperAdmin) {
      if (currentlyHasPermission && !canDeleteAssignments) {
        toast.error('You do not have permission to remove permissions from employees');
        return;
      }

      if (!currentlyHasPermission && !canAddAssignments) {
        toast.error('You do not have permission to add permissions to employees');
        return;
      }
    }

    setUnsavedChanges(true);

    if (currentlyHasPermission) {
      // Remove permission
      removePermissionMutation.mutate({
        employeeUuid: employee.uuid,
        permissionUuid
      });
      setEmployeePermissions(prev => prev.filter(p => p !== permissionKey));
    } else {
      // Add permission
      assignPermissionMutation.mutate({
        employeeUuid: employee.uuid,
        permissionUuid
      });
      setEmployeePermissions(prev => [...prev, permissionKey]);
    }
  };



  // Toggle module expansion
  const toggleModule = (moduleKey) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  // Get permission count for module
  const getModulePermissionCount = (moduleKey) => {
    const modulePermissions = filteredPermissionConfig.modules[moduleKey]?.permissions || [];
    const hasCount = modulePermissions.filter(p => employeeHasPermission(p.key)).length;
    return { has: hasCount, total: modulePermissions.length };
  };

  // Toggle all permissions in module
  const toggleAllModulePermissions = (moduleKey) => {
    const modulePermissions = filteredPermissionConfig.modules[moduleKey]?.permissions || [];
    const { has, total } = getModulePermissionCount(moduleKey);
    
    if (has === total) {
      // Remove all permissions in this module
      if (!isSuperAdmin && !canDeleteAssignments) {
        toast.error('You do not have permission to remove permissions from employees');
        return;
      }
      modulePermissions.forEach(permission => {
        if (employeePermissions.includes(permission.key)) {
          togglePermission(permission.key);
        }
      });
    } else {
      // Add all missing permissions in this module
      if (!isSuperAdmin && !canAddAssignments) {
        toast.error('You do not have permission to add permissions to employees');
        return;
      }
      modulePermissions.forEach(permission => {
        if (!employeePermissions.includes(permission.key)) {
          togglePermission(permission.key);
        }
      });
    }
  };

  if (employeeLoading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-lg font-medium mb-2">Employee not found</div>
          <button
            onClick={() => navigate('/employees')}
            className="text-indigo-600 hover:text-indigo-800"
          >
            Return to Employees
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="card-gradient shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between py-4 sm:py-6 gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <button
                onClick={() => navigate('/employees')}
                className="flex items-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 text-sm sm:text-base whitespace-nowrap transition-colors"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                Back to Employees
              </button>
              <div className="flex items-center space-x-3 sm:space-x-4 min-w-0">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-glow">
                  <User className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-lg sm:text-xl font-semibold gradient-text truncate">
                    Manage Permissions - {employee.name}
                  </h1>
                  <p className="text-sm text-gray-600 dark:text-gray-300 truncate">{employee.email}</p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              {unsavedChanges && (
                <span className="text-xs sm:text-sm text-orange-600 bg-orange-50 px-2 py-1 rounded whitespace-nowrap">
                  Unsaved changes
                </span>
              )}
              <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
                <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded whitespace-nowrap">
                  {employeePermissions.length} assigned
                </span>
                <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded whitespace-nowrap">
                  {allPermissions.length} available
                </span>
                {isSuperAdmin && (
                  <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded whitespace-nowrap">
                    Super Admin
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {/* Sidebar - Employee Info */}
          <div className="xl:col-span-1">
            {/* Employee Info */}
            <div className="card-modern">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 sm:mb-4">Employee Details</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 min-w-0">
                  <Mail className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{employee.email}</span>
                </div>
                {employee.phone && (
                  <div className="flex items-center space-x-3 min-w-0">
                    <Phone className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <span className="text-sm text-gray-600 dark:text-gray-300 truncate">{employee.phone}</span>
                  </div>
                )}
                <div className="flex items-center space-x-3">
                  <Users className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                  <span className={`text-xs sm:text-sm px-2 py-1 rounded-full whitespace-nowrap ${
                    employee.role === 'developer' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' :
                    employee.role === 'admin' ? 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400' :
                    employee.role === 'manager' ? 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400' :
                    'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'
                  }`}>
                    {employee.role.charAt(0).toUpperCase() + employee.role.slice(1)}
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Shield className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className={`text-xs sm:text-sm px-2 py-1 rounded-full whitespace-nowrap ${
                    employee.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {employee.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                {/* Current User's Access Level */}
                <div className="border-t pt-3 mt-3">
                  <div className="flex items-center space-x-3">
                    <UserCheck className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-gray-500 mb-1">Your Access Level</div>
                      <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                        isSuperAdmin 
                          ? 'bg-purple-100 text-purple-800' 
                          : canAddAssignments || canDeleteAssignments 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                      }`}>
                        {isSuperAdmin 
                          ? 'Super Admin' 
                          : canAddAssignments || canDeleteAssignments 
                            ? 'Can Modify' 
                            : 'View Only'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content - Permissions */}
          <div className="xl:col-span-3">
            {/* View-Only Banner */}
            {canViewAssignments && !canAddAssignments && !canDeleteAssignments && !isSuperAdmin && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-center">
                  <Info className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-900">View Only Mode</h4>
                    <p className="text-sm text-blue-700 mt-1">
                      You can view this employee's permissions but cannot make changes. Contact an administrator to add or remove permissions.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="p-4 sm:p-6 border-b">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Module Permissions</h3>
                <p className="text-sm text-gray-600 mt-1 break-words">
                  {isSuperAdmin ? (
                    <>
                      <span className="font-medium text-purple-600">Super Admin Access:</span> You can view and assign all system permissions. Each module contains specific actions like Create, View, Update, Edit, and Delete. Click on permissions to toggle access.
                    </>
                  ) : canAddAssignments || canDeleteAssignments ? (
                    <>
                      <span className="font-medium text-green-600">Assignment Access:</span> You can {canAddAssignments ? 'add' : ''}{canAddAssignments && canDeleteAssignments ? ' and ' : ''}{canDeleteAssignments ? 'remove' : ''} permissions for this employee. Click on permissions to toggle access.
                    </>
                  ) : canViewAssignments ? (
                    <>
                      <span className="font-medium text-blue-600">View Only Access:</span> You can view this employee's permissions but cannot modify them. Contact an administrator to make changes.
                    </>
                  ) : (
                    <>
                      Manage permissions by module. Each module contains specific actions like Create, View, Update, Edit, and Delete.
                    </>
                  )}
                </p>
                <div className="mt-3 flex flex-wrap gap-1 sm:gap-2">
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full whitespace-nowrap">View - Read</span>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full whitespace-nowrap">Create - Add</span>
                  <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full whitespace-nowrap">Update - Modify</span>
                  <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full whitespace-nowrap">Edit - Details</span>
                  <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full whitespace-nowrap">Delete - Remove</span>
                  {isSuperAdmin && (
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full whitespace-nowrap">Assign - Manage</span>
                  )}
                </div>
              </div>

              <div className="p-4 sm:p-6">
                <div className="space-y-4 sm:space-y-6">
                  {Object.entries(filteredPermissionConfig.modules).length === 0 ? (
                    <div className="text-center py-8">
                      <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No Permissions Available</h3>
                      <p className="text-gray-600 mb-4">
                        {permissionsLoading 
                          ? 'Loading available permissions...' 
                          : 'No permissions are available to assign to this employee.'}
                      </p>
                      {!permissionsLoading && (
                        <button
                          onClick={() => navigate('/employees')}
                          className="text-indigo-600 hover:text-indigo-800 font-medium"
                        >
                          Back to Employees
                        </button>
                      )}
                    </div>
                  ) : (
                    Object.entries(filteredPermissionConfig.modules).map(([moduleKey, module]) => {
                    const { has, total } = getModulePermissionCount(moduleKey);
                    const isExpanded = expandedModules[moduleKey];
                    const allGranted = has === total;
                    const someGranted = has > 0 && has < total;

                    return (
                      <div key={moduleKey} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Module Header */}
                        <div className="p-3 sm:p-4 bg-gray-50 border-b">
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="flex items-center space-x-3 min-w-0 flex-1">
                              <span className="text-xl sm:text-2xl flex-shrink-0">{module.icon}</span>
                              <div className="min-w-0 flex-1">
                                <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{module.name}</h4>
                                <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 break-words">{module.description}</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4">
                              <div className="flex items-center space-x-2">
                                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                                  allGranted ? 'bg-green-500' : 
                                  someGranted ? 'bg-yellow-500' : 'bg-gray-300'
                                }`} />
                                <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                  {has}/{total}
                                </span>
                              </div>
                              <button
                                onClick={() => toggleAllModulePermissions(moduleKey)}
                                disabled={!isSuperAdmin && !canAddAssignments && !canDeleteAssignments}
                                className={`text-xs px-2 sm:px-3 py-1 rounded-full whitespace-nowrap ${
                                  !isSuperAdmin && !canAddAssignments && !canDeleteAssignments
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : allGranted 
                                      ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                                }`}
                              >
                                {allGranted ? 'Remove All' : 'Grant All'}
                              </button>
                              <button
                                onClick={() => toggleModule(moduleKey)}
                                className="text-gray-500 hover:text-gray-700 flex-shrink-0"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                                )}
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Module Permissions */}
                        {isExpanded && (
                          <div className="p-3 sm:p-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-3">
                              {module.permissions.map((permission) => {
                                const actionType = permission.key.split('_')[1] || 'action';
                                const actionColors = {
                                  'view': 'bg-blue-100 text-blue-800',
                                  'create': 'bg-green-100 text-green-800',
                                  'update': 'bg-yellow-100 text-yellow-800',
                                  'edit': 'bg-orange-100 text-orange-800',
                                  'delete': 'bg-red-100 text-red-800',
                                  'assign': 'bg-purple-100 text-purple-800'
                                };
                                
                                const canToggle = isSuperAdmin || canAddAssignments || canDeleteAssignments;
                                const currentlyHasPermission = employeePermissions.includes(permission.key);
                                
                                return (
                                  <div
                                    key={permission.key}
                                    className={`p-3 rounded-lg border transition-all duration-200 overflow-hidden ${
                                      currentlyHasPermission
                                        ? 'border-green-300 bg-green-50 shadow-sm'
                                        : canToggle 
                                          ? 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
                                          : 'border-gray-200 bg-gray-50'
                                    } ${canToggle ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                                    onClick={() => canToggle && togglePermission(permission.key)}
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex-1 min-w-0">
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                                          <h5 className="font-medium text-gray-900 text-sm break-words">{permission.name}</h5>
                                          <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap flex-shrink-0 ${actionColors[actionType] || 'bg-gray-100 text-gray-600'}`}>
                                            {actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                                          </span>
                                        </div>
                                        <p className="text-xs text-gray-600 leading-tight break-words line-clamp-2">{permission.description}</p>
                                      </div>
                                      <div className={`flex items-center justify-center w-5 h-5 rounded-full flex-shrink-0 ${
                                        currentlyHasPermission
                                          ? 'bg-green-500 text-white'
                                          : 'bg-gray-300 text-gray-500'
                                      }`}>
                                        {currentlyHasPermission ? (
                                          <Check className="w-3 h-3" />
                                        ) : (
                                          <X className="w-3 h-3" />
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeePermissions;